import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import AdminNavbar from '../components/AdminNavbar';
import {
    ArrowLeft,
    Download,
    Search,
    Filter,
    ExternalLink,
    CheckCircle,
    Clock,
    XCircle,
    RefreshCw
} from 'lucide-react';

const AdminDepositsPage = () => {
    const [deposits, setDeposits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });

    useEffect(() => {
        fetchDeposits();
    }, [statusFilter]);

    const fetchDeposits = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (statusFilter) params.append('status', statusFilter);
            params.append('limit', pagination.limit);
            params.append('offset', pagination.offset);

            const res = await api.get(`/admin/deposits?${params.toString()}`);
            setDeposits(res.data.deposits || []);
            setPagination(res.data.pagination || pagination);
        } catch (error) {
            console.error('Fetch deposits error:', error);
            toast.error('Failed to load deposits');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            completed: 'bg-green-500/20 text-green-400 border-green-500/30',
            pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            confirmed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            failed: 'bg-red-500/20 text-red-400 border-red-500/30'
        };
        const icons = {
            completed: <CheckCircle size={14} />,
            pending: <Clock size={14} />,
            confirmed: <CheckCircle size={14} />,
            failed: <XCircle size={14} />
        };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
                {icons[status] || icons.pending}
                {status?.charAt(0).toUpperCase() + status?.slice(1)}
            </span>
        );
    };

    const filteredDeposits = deposits.filter(d => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            d.user?.email?.toLowerCase().includes(search) ||
            d.token?.symbol?.toLowerCase().includes(search) ||
            d.txHash?.toLowerCase().includes(search) ||
            d.fromAddress?.toLowerCase().includes(search)
        );
    });

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />

            {/* Header */}
            <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/admin" className="text-gray-400 hover:text-white transition">
                            <ArrowLeft size={24} />
                        </Link>
                        <div>
                            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                                <Download className="text-green-400" /> Crypto Deposits
                            </h1>
                            <p className="text-gray-400 text-sm">View all cryptocurrency deposit transactions</p>
                        </div>
                    </div>
                    <button
                        onClick={fetchDeposits}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
                    >
                        <RefreshCw size={18} /> Refresh
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="px-6 py-4 bg-slate-800/50 border-b border-slate-700">
                <div className="flex flex-wrap gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search by email, token, tx hash, or address..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    >
                        <option value="">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                        <option value="failed">Failed</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="p-6">
                <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                    {loading ? (
                        <div className="text-center py-12 text-gray-400">Loading deposits...</div>
                    ) : filteredDeposits.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">No deposits found</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-900/50">
                                    <tr className="text-left text-gray-400 text-sm">
                                        <th className="px-4 py-3 font-medium">User</th>
                                        <th className="px-4 py-3 font-medium">Token</th>
                                        <th className="px-4 py-3 font-medium">Amount</th>
                                        <th className="px-4 py-3 font-medium">Network</th>
                                        <th className="px-4 py-3 font-medium">Status</th>
                                        <th className="px-4 py-3 font-medium">Tx Hash</th>
                                        <th className="px-4 py-3 font-medium">Date</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700">
                                    {filteredDeposits.map((deposit) => (
                                        <tr key={deposit.id} className="hover:bg-slate-700/30 transition">
                                            <td className="px-4 py-3">
                                                <div>
                                                    <div className="text-white font-medium">{deposit.user?.email || 'Unknown'}</div>
                                                    <div className="text-xs text-gray-400">
                                                        {deposit.user?.firstName} {deposit.user?.lastName}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-2 px-2 py-1 bg-slate-700 rounded text-white font-medium">
                                                    {deposit.token?.symbol || 'Unknown'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-white font-mono">
                                                {parseFloat(deposit.amount).toFixed(6)}
                                            </td>
                                            <td className="px-4 py-3 text-gray-300">
                                                {deposit.network || deposit.metadata?.network || '-'}
                                            </td>
                                            <td className="px-4 py-3">
                                                {getStatusBadge(deposit.status)}
                                            </td>
                                            <td className="px-4 py-3">
                                                {deposit.txHash ? (
                                                    <a
                                                        href={`https://etherscan.io/tx/${deposit.txHash}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-400 hover:text-blue-300 text-xs font-mono flex items-center gap-1"
                                                    >
                                                        {deposit.txHash.slice(0, 10)}...{deposit.txHash.slice(-6)}
                                                        <ExternalLink size={12} />
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-500">-</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-gray-400 text-sm">
                                                {new Date(deposit.createdAt).toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Pagination Info */}
                <div className="mt-4 text-center text-gray-400 text-sm">
                    Showing {filteredDeposits.length} of {pagination.total} deposits
                </div>
            </div>
        </div>
    );
};

export default AdminDepositsPage;
